import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Breadcrumbs, SuggestedArticles } from '@/components';
import { useBrand } from '@/contexts/BrandContext';
import { startDeflection, updateDeflection, submitSupportRequest, getErrorCode } from '@/services/api';
import { Button, ErrorState } from '@airclou/ui';
import { newIdempotencyKey } from '@/utils';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export function RequestPage() {
  const { brand, viewer, isAuthenticated } = useBrand();
  const navigate = useNavigate();

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(viewer?.email || '');
  const [state, setState] = useState<FormState>('idle');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // One key per submission, resent unchanged on a retry, so a submission whose
  // answer was lost does not create a second ticket. A new key whenever the
  // form changes: an edited retry is a different request, and reusing the key
  // would hand back the first ticket with the old text and address.
  const idempotencyKey = useRef<string | null>(null);
  useEffect(() => {
    idempotencyKey.current = null;
  }, [subject, message, email]);
  
  // Deflection tracking
  const sessionId = useRef(`session_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const deflectionId = useRef<string | null>(null);
  const suggestedArticleIds = useRef<string[]>([]);

  const combinedQuery = `${subject} ${message}`.trim();

  // Start deflection tracking when user starts typing
  useEffect(() => {
    if ((subject.trim() || message.trim()) && !deflectionId.current) {
      // Will be started on first article click or form submit
    }
  }, [subject, message]);

  const handleSuggestionsLoad = (articleIds: string[]) => {
    suggestedArticleIds.current = articleIds;
  };

  const handleArticleClick = async (articleId: string) => {
    try {
      // Start deflection tracking if not already started
      if (!deflectionId.current) {
        const result = await startDeflection({
          session_id: sessionId.current,
          search_query: combinedQuery,
          form_subject: subject,
          form_body: message,
          suggested_article_ids: suggestedArticleIds.current,
        });
        deflectionId.current = result.deflection_id;
      }
      
      // Update deflection with clicked article (potential deflection)
      await updateDeflection({
        deflection_id: deflectionId.current,
        clicked_article_id: articleId,
        deflected: true, // Assume deflected until they submit a ticket
      });
    } catch {
      // Ignore deflection tracking errors
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !message.trim()) {
      return;
    }

    if (!isAuthenticated && !email.trim()) {
      return;
    }

    setState('submitting');
    setErrorCode(null);

    try {
      // Start deflection tracking if not already started
      if (!deflectionId.current) {
        const result = await startDeflection({
          session_id: sessionId.current,
          search_query: combinedQuery,
          form_subject: subject,
          form_body: message,
          suggested_article_ids: suggestedArticleIds.current,
        });
        deflectionId.current = result.deflection_id;
      }
      
      // Submit support request
      idempotencyKey.current ??= newIdempotencyKey();
      await submitSupportRequest({
        subject,
        message,
        email: isAuthenticated ? undefined : email,
        deflection_id: deflectionId.current || undefined,
        idempotencyKey: idempotencyKey.current,
      });
      
      setState('success');
    } catch (error) {
      setState('error');
      setErrorCode(getErrorCode(error));
    }
  };

  if (state === 'success') {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <div className="max-w-xl mx-auto text-center py-12">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Request submitted
          </h1>
          <p className="text-muted-foreground mb-6">
            Thank you for reaching out. We'll get back to you as soon as possible.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/requests')}>View my requests</Button>
            <Button variant="outline" onClick={() => navigate('/')}>Back to Help Center</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Contact support' },
        ]}
      />

      <div className="max-w-3xl">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
          Contact support
        </h1>
        <p className="text-muted-foreground mb-8">
          Can't find what you're looking for? Send us a message and we'll help you out.
        </p>

        {state === 'error' && (
          <div className="mb-6">
            <ErrorState
              description={
                errorCode === 'RATE_LIMITED'
                  ? 'Too many requests. Please wait a moment and try again.'
                  : 'Failed to submit your request. Please try again.'
              }
              action={
                <button
                  onClick={() => setState('idle')}
                  className="text-primary hover:underline"
                >
                  Try again
                </button>
              }
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
            {!isAuthenticated && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Your email <span className="text-destructive">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full h-10 px-3 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}

            <div>
              <label htmlFor="subject" className="block text-sm font-medium mb-2">
                Subject <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                placeholder="What do you need help with?"
                className="w-full h-10 px-3 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-2">
                Message <span className="text-destructive">*</span>
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={6}
                placeholder="Describe your issue in detail..."
                className="w-full px-3 py-2 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={state === 'submitting'}
              className="w-full sm:w-auto"
            >
              {state === 'submitting' && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Submit request
            </Button>
          </form>

          {/* Suggested Articles */}
          <div className="lg:col-span-1">
            <SuggestedArticles
              query={combinedQuery}
              onArticleClick={handleArticleClick}
              onSuggestionsLoad={handleSuggestionsLoad}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
